<template>
  <label
    :class="[
      ns.b(),
      ns.is('disabled', disabled),
      ns.is('focus', focus),
      ns.is('bordered', border),
      ns.is('checked', modelValue === label),
      ns.m(size),
      rawAttrs.class,
    ]"
    :style="containerStyle"
    data-component-name="Radio"
    :data-event-name="trackEventName"
    :data-event-content="trackEventContent"
    v-bind="labelListieners"
  >
    <span
      :class="[
        ns.e('input'),
        ns.is('disabled', disabled),
        ns.is('checked', modelValue === label),
      ]"
    >
      <input
        ref="radioRef"
        v-model="modelValue"
        :class="ns.e('original')"
        :value="label"
        :name="name || radioGroup?.name"
        :disabled="disabled"
        type="radio"
        v-bind="inputAttrs"
        @focus="focus = true"
        @blur="focus = false"
        @change="handleChange"
        @click.stop
      />
      <span :class="ns.e('inner')" />
    </span>
    <span :class="ns.e('label')" @keydown.stop>
      <slot>
        {{ label }}
      </slot>
    </span>
  </label>
</template>

<script lang="ts" setup>
import { computed, nextTick, useAttrs as useRawAttrs, useSlots } from 'vue'
import { useAttrs, useNamespace } from '@jdesign-vue/hooks'
import { radioEmits, radioProps } from './radio'
import { useRadio } from './use-radio'

import type { StyleValue } from 'vue'

defineOptions({
  name: 'JdRadio',
  inheritAttrs: false,
})

const props = defineProps(radioProps)
const emit = defineEmits(radioEmits)
const rawAttrs = useRawAttrs()
const containerStyle = computed<StyleValue>(() => rawAttrs.style as StyleValue)
const inputAttrs = useAttrs({ excludeListeners: true })
const labelListieners = useAttrs({
  excludeKeys: computed<string[]>(() => {
    return Object.keys(inputAttrs.value)
  }),
})

const ns = useNamespace('radio')
const slots = useSlots()
const { radioRef, radioGroup, focus, size, disabled, modelValue } = useRadio(
  props,
  emit
)

function getNodeText(node: any): string {
  if (node == null) return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(getNodeText).join('')
  return getNodeText(node.children)
}

const trackText = computed(() => {
  const text = getNodeText(slots.default?.() ?? []).trim()
  if (text) return text
  if (props.label != null) return String(props.label)
  return '单选框'
})

const isChecked = computed(() => modelValue.value === props.label)

const trackEventName = computed(() =>
  isChecked.value ? 'unchecked' : 'checked'
)

const trackEventContent = computed(() =>
  `${isChecked.value ? '取消选中' : '选中'}${trackText.value}`
)

function handleChange() {
  nextTick(() => emit('change', modelValue.value))
}
</script>
